import React, { FC, useEffect, useState } from 'react';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { LoginModal } from '../../components/login/LoginModal';
import { VerificationCodeForm } from '../../components/profileSettings/forms/VerificationCodeForm';
import eventEmitter from '../../utils/loginModalState';
import { CodeModalProps } from '../../views/login/Login';
import * as auth from '../../redux/AuthRedux';
import { useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { verifyLoginCodeAuthentication } from '../../store/identyti/actions/security';
import { useIdentity } from '../../store/identyti/identity-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { LinkComponent } from '../../components/LinkComponent';
import { useTranslation } from 'react-i18next';
import { IAuthState } from '../../redux/AuthRedux';
import { RootState } from '../../../setup';
import { useLocation } from 'react-router';
import { checkIfValidUUID } from './PageData';

export const LoginLayout: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { pathname } = useLocation();


	const exception = (): boolean => {
		const pathnameArr = pathname.split('/');
		const secondEl = pathnameArr[2];
		const lastEl = pathnameArr[pathnameArr.length-1];

		if (
			pathname === '/accounts/applications/new-application'
			|| (secondEl === 'applications' && checkIfValidUUID(lastEl))
			|| lastEl === 'business-information'
			|| lastEl === 'address'
			|| lastEl === 'contact-information'
			|| lastEl === 'customer-support'
			|| lastEl === 'developer-details'
			|| lastEl === 'bank'
		) {
			return true;
		} else {
			return false;
		}
	};

	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const userData = authUser.userData;

	const { identityState: { isVerificationLoginCodeLoading }, dispatch: identityDispatch } = useIdentity();


	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [isVisible, setIsVisible] = useState<boolean>();
	const [codeModalState, setCodeModalState] = useState<CodeModalProps>({
		isOpen: false,
		session: '',
		email: ''
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));
	const closeCodeModal = (): void => setCodeModalState(prev => ({ ...prev, isOpen: false }));
	const closeConfirmPasswordModal = (): void => setIsVisible(false);

	const handleLogOut = (): void => {
		closeConfirmPasswordModal();
		console.log('logout---014');
		dispatch(auth.actions.logout());
		navigate('/');
	};

	useEffect(() => {
		const openModalListener = (): void => {
			setIsVisible(true);
		};

		const closeModalListener = (): void => {
			setIsVisible(false);
		};

		eventEmitter.on('openModal', openModalListener);
		eventEmitter.on('closeModal', closeModalListener);

		return () => {
			eventEmitter.removeListener('openModal', openModalListener);
			eventEmitter.removeListener('closeModal', closeModalListener);
		};
	}, []);

	const hideRecaptcha = (): void => {
		const script = document.querySelector('.grecaptcha-badge');
		if (script) {
			// @ts-ignore
			script.style.visibility = 'hidden';
		}
	};

	const handleConfirmCode = async (code: string, session: string, email: string, rememberMFA: boolean): Promise<void> => {
		const params = {
			email,
			session,
			code,
			rememberMFA
		};

		await verifyLoginCodeAuthentication(identityDispatch, params, handleErrorModal, closeCodeModal, handleSuccessLogin);
	};

	const handleSuccessLogin = (user): void => {
		const loginData = {
			accessToken: user.data?.accessToken,
			idToken: user.data.id,
			userData
		};
		dispatch(auth.actions.login(loginData));
		hideRecaptcha();
		closeModal();
		if (exception()) {
			closeConfirmPasswordModal();
		} else {
			navigate(0);
		}
	};

	const handleErrorModal = (): void => {
		closeCodeModal();
		setModalState({
			isOpen: true,
			modalTitle: 'Use an authenticator app',
			content: (
				<div>
					<div className="fs-3 text-danger text-center mb-5">
						<FontAwesomeIcon icon={faTriangleExclamation} className="me-1"/>
						Incorrect verification code.
					</div>
					<div className="fs-6 px-15 text-center">
						Having trouble entering your code? Please, contact with {' '}
						<LinkComponent text="administrator." link='mailto:support@flex-charge.com'/>
					</div>

				</div>
			),
			confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				closeModal();
				setCodeModalState(prev => ({ ...prev, isOpen: true }));
			},
			onClose: closeModal
		});
	};

	return (
		<>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={isVisible}
				modalTitle='Confirm password'
				onClose={handleLogOut}
				contentClassName="d-block"
				minHeight={50}
				backdrop="static"
				content={
					<LoginModal
						closeModal={closeConfirmPasswordModal}
						logOut={handleLogOut}
						setCodeModalState={setCodeModalState}
						handleSuccessLogin={handleSuccessLogin}
					/>
				}
			/>
			<ModalWindow
				isOpen={codeModalState.isOpen}
				modalTitle="Two-step authentication"
				backdrop="static"
				contentClassName='d-block'
				minHeight={260}
				onClose={closeCodeModal}
				content={
					<VerificationCodeForm
						onContinue={handleConfirmCode}
						isLoading={isVerificationLoginCodeLoading}
						session={codeModalState?.session}
						email={codeModalState?.email}
						showCheckbox
					/>
				}
			/>
		</>
	);
};
